import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { DisplayModes } from 'services/webedi/types';
import { cn } from 'support/helpers/cn/cn';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { WebEDIRowData } from 'support/types/webediTable';
import { useValue } from 'components/WebEDI/helpers';

import { overflowTooltipTarget } from '../../../components/OverflowTooltip/overflowTooltipTarget';
import { CellProps } from '../../types';
import { getCellNestedLevel, useCellClassNames, useNumberConfigurations } from '../cellHelpers';
import { NestedLevelAddon } from '../components/NestedLevelAddon/NestedLevelAddon';
import { ViewWrapperCell } from './ViewWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> &
  Pick<CellProps, 'fieldId' | 'isHighlighted' | 'diffStatus' | 'originalValue' | 'wasAIEdited'>;

export const ViewNumberCell = ({
  row,
  cell,
  column,
  table,
  isHighlighted,
  fieldId,
  diffStatus,
  originalValue,
  wasAIEdited,
}: Props) => {
  const { t } = useTranslation();
  const {
    fieldState: { error },
  } = useController({ name: fieldId });

  const {
    columnDef: { meta: columnMeta },
  } = column;

  const optionalConfig = columnMeta?.optionalConfig;
  const value = useValue(row.original, fieldId, optionalConfig?.expression);
  const { prefix, suffix, formattedValue } = useNumberConfigurations({
    meta: columnMeta,
    value,
    mode: DisplayModes.view,
    rowData: row.original,
    fieldId,
  });
  const {
    prefix: basePrefix,
    formattedValue: baseFormattedValue,
    suffix: baseSuffix,
  } = useNumberConfigurations({
    meta: columnMeta,
    value: originalValue ?? undefined,
    mode: DisplayModes.view,
    rowData: row.original,
    fieldId,
  });
  const formattedBaseValue = baseFormattedValue ? `${basePrefix}${baseFormattedValue}${baseSuffix}` : undefined;

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });
  const cellNestedLevel = columnMeta?.showNestLevel ? getCellNestedLevel(row) : 0;

  return (
    <ViewWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      cell={cell}
      diffStatus={diffStatus}
      originalValue={originalValue}
      formattedOriginalValue={formattedBaseValue}
      textAlign={optionalConfig?.textAlign !== 'left' ? 'right' : 'left'}
      wasAIEdited={wasAIEdited}
    >
      <span className="flex w-full min-w-0 items-center">
        <div
          className={cn('flex w-full min-w-0 gap-x-0.5 overflow-hidden tabular-nums', {
            'justify-end': optionalConfig?.textAlign !== 'left',
            'justify-start': optionalConfig?.textAlign === 'left',
          })}
        >
          {cellNestedLevel > 1 ? <NestedLevelAddon level={cellNestedLevel} className="pr-1.5" /> : null}
          {!formattedValue && error?.type === 'required' ? (
            t('common:table.errors.requiredField')
          ) : formattedValue ? (
            <span className="flex-initial truncate" {...overflowTooltipTarget}>
              {prefix && <span className={cn({ 'text-gray-500': !error })}>{prefix}</span>}
              {formattedValue}
              {suffix && <span className={cn({ 'text-gray-500': !error })}>{suffix}</span>}
            </span>
          ) : (
            <span className={cn({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
          )}
        </div>
      </span>
    </ViewWrapperCell>
  );
};
